import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Seo from '../components/Seo';
import PaymentGateway from '../components/PaymentGateway';
import {
  createPropertyBooking,
  formatGBP,
  getImageUrl,
  getPropertyListingById,
  initiatePropertyPayment,
} from '../services/api';

const sectionCardSx = {
  borderRadius: 5,
  border: '1px solid rgba(15, 23, 42, 0.08)',
  boxShadow: '0 20px 45px rgba(15, 23, 42, 0.08)',
};

const PropertyBooking = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [property, setProperty] = useState(location.state?.property || null);
  const [loading, setLoading] = useState(!location.state?.property);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [bookingResponse, setBookingResponse] = useState(null);
  const [payFastData, setPayFastData] = useState(null);
  const [showGateway, setShowGateway] = useState(false);
  const cancelledStatus = searchParams.get('status');

  const [formData, setFormData] = useState({
    bookingType: location.state?.bookingType || 'Rent',
    customerFirstName: '',
    customerLastName: '',
    customerEmail: '',
    customerPhone: '',
    customerAddress: '',
    customerCity: '',
    customerCountry: 'United Kingdom',
    customerPostalCode: '',
    moveInDate: '',
    moveOutDate: '',
    numberOfAdults: 1,
    numberOfChildren: 0,
    paymentMethod: 'PayFast',
    notes: '',
  });

  useEffect(() => {
    if (property) return;

    const loadProperty = async () => {
      try {
        setLoading(true);
        const response = await getPropertyListingById(id);
        setProperty(response?.data || null);
      } catch (err) {
        console.error('Failed to load property for booking:', err);
        setError('Failed to load property booking details.');
      } finally {
        setLoading(false);
      }
    };

    loadProperty();
  }, [id, property]);

  const totals = useMemo(() => {
    const unitPrice = Number(property?.price || 0);
    const subtotal = unitPrice;
    const taxRate = 0.05;
    const taxAmount = Math.round(subtotal * taxRate * 100) / 100;
    const totalAmount = subtotal + taxAmount;

    return {
      unitPrice,
      subtotal,
      taxAmount,
      totalAmount,
    };
  }, [property]);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const isFormValid = () => {
    return Boolean(
      property &&
      formData.customerFirstName.trim() &&
      formData.customerLastName.trim() &&
      formData.customerEmail.trim() &&
      formData.paymentMethod.trim()
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!property) return;

    try {
      setSubmitting(true);
      setError('');

      const payload = {
        propertyListingId: Number(property.id),
        bookingType: formData.bookingType,
        customerFirstName: formData.customerFirstName.trim(),
        customerLastName: formData.customerLastName.trim(),
        customerEmail: formData.customerEmail.trim(),
        customerPhone: formData.customerPhone.trim(),
        customerAddress: formData.customerAddress.trim(),
        customerCity: formData.customerCity.trim(),
        customerCountry: formData.customerCountry.trim(),
        customerPostalCode: formData.customerPostalCode.trim(),
        moveInDate: formData.moveInDate || null,
        moveOutDate: formData.moveOutDate || null,
        numberOfAdults: Number(formData.numberOfAdults || 0),
        numberOfChildren: Number(formData.numberOfChildren || 0),
        quantity: 1,
        unitPrice: totals.unitPrice,
        subtotal: totals.subtotal,
        taxAmount: totals.taxAmount,
        totalAmount: totals.totalAmount,
        paymentMethod: formData.paymentMethod,
        notes: formData.notes.trim(),
      };

      const created = await createPropertyBooking(payload);
      const createdData = created?.data;
      setBookingResponse(createdData || null);

      if (!createdData?.bookingId) {
        throw new Error('Booking response was incomplete.');
      }

      const paymentInit = await initiatePropertyPayment({
        propertyBookingId: createdData.bookingId,
        paymentMethod: formData.paymentMethod,
      });

      if (formData.paymentMethod === 'CashCounter') {
        navigate('/properties/thank-you', {
          state: {
            propertyBooking: createdData,
            property,
            formData,
            totals,
            paymentMethod: 'CashCounter',
          },
        });
        return;
      }

      if (formData.paymentMethod === 'Stripe' && paymentInit?.clientSecret) {
        window.location.href = paymentInit.clientSecret;
        return;
      }

      if (formData.paymentMethod === 'PayFast' && paymentInit?.payFastPayload) {
        setPayFastData(paymentInit.payFastPayload);
        setShowGateway(true);
        return;
      }

      navigate('/properties/thank-you', {
        state: {
          propertyBooking: createdData,
          property,
          formData,
          totals,
          paymentMethod: formData.paymentMethod,
        },
      });
    } catch (err) {
      console.error('Property booking failed:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to complete property booking.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', pt: 14, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress size={56} />
      </Box>
    );
  }

  if (!property) {
    return (
      <Box sx={{ minHeight: '100vh', pt: 14 }}>
        <Container maxWidth="lg" sx={{ textAlign: 'center', py: 10 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>Property booking unavailable</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>{error || 'This property could not be loaded for booking.'}</Typography>
          <Button component={Link} to="/properties" variant="contained">Back to Properties</Button>
        </Container>
      </Box>
    );
  }

  if (showGateway && payFastData) {
    return (
      <Box sx={{ minHeight: '100vh', pt: 14, backgroundColor: '#f8fafc' }}>
        <Container maxWidth="md" sx={{ py: 6 }}>
          <Paper sx={{ p: 4, borderRadius: 3 }}>
            <PaymentGateway paymentData={payFastData} onError={setError} />
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc', pt: 14, pb: 8 }}>
      <Seo
        title={`Book ${property.title}`}
        description={`Complete your ${formData.bookingType.toLowerCase()} booking for ${property.title} with secure payment options.`}
      />
      <Container maxWidth="xl">
        <Box sx={{ mb: 3 }}>
          <Typography component={Link} to={`/properties/${property.id}`} sx={{ textDecoration: 'none', color: 'primary.main', fontWeight: 600 }}>
            ← Back to Property Details
          </Typography>
        </Box>

        {cancelledStatus === 'cancelled' ? (
          <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
            Your payment session was cancelled. You can try again below.
          </Alert>
        ) : null}

        {error ? (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        ) : null}

        <Grid
          container
          spacing={{ xs: 3, lg: 3 }}
          alignItems="flex-start"
          wrap={{ xs: 'wrap', lg: 'nowrap' }}
        >
          <Grid item xs={12} lg="auto" sx={{ width: { lg: 340 }, flexShrink: 0 }}>
            <Paper sx={{ p: 4, position: { lg: 'sticky' }, top: 120, ...sectionCardSx }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>Booking Summary</Typography>
                <Chip label={formData.bookingType} color="primary" sx={{ fontWeight: 700 }} />
              </Stack>

              <Card sx={{ mb: 3, borderRadius: 4, overflow: 'hidden', boxShadow: 'none', border: '1px solid rgba(15, 23, 42, 0.08)' }}>
                <Box component="img" src={getImageUrl(property.primaryImagePath || '/Gallery/Items/lobby-1.jpg')} alt={property.title} sx={{ width: '100%', height: 220, objectFit: 'cover' }} />
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{property.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{property.location}{property.city ? `, ${property.city}` : ''}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>{property.purpose || formData.bookingType} • {property.propertyType || 'Property'}</Typography>
                </CardContent>
              </Card>

              <Stack spacing={2} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography color="text.secondary">Unit Price</Typography>
                  <Typography fontWeight={700}>{formatGBP(totals.unitPrice)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography color="text.secondary">Subtotal</Typography>
                  <Typography fontWeight={700}>{formatGBP(totals.subtotal)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography color="text.secondary">Tax</Typography>
                  <Typography fontWeight={700}>{formatGBP(totals.taxAmount)}</Typography>
                </Box>
              </Stack>

              <Divider sx={{ my: 2.5 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>Total</Typography>
                <Typography variant="h4" sx={{ fontWeight: 900, color: 'primary.main' }}>{formatGBP(totals.totalAmount)}</Typography>
              </Box>

              <Alert severity="info" sx={{ borderRadius: 3 }}>
                {formData.paymentMethod === 'CashCounter'
                  ? 'Cash Counter bookings are created first and completed by admin verification.'
                  : formData.paymentMethod === 'Stripe'
                    ? 'Stripe will open a secure hosted checkout after booking creation.'
                    : 'PayFast will open its secure payment gateway after booking creation.'}
              </Alert>
            </Paper>
          </Grid>

          <Grid item xs={12} lg sx={{ minWidth: 0, flex: 1 }}>
            <Paper sx={{ p: { xs: 3, md: 4 }, ...sectionCardSx }}>
              <Stack spacing={1} sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  Complete Property Booking
                </Typography>
                <Typography color="text.secondary">
                  Enter your details below and choose a payment method to continue with your {formData.bookingType.toLowerCase()} booking.
                </Typography>
              </Stack>

              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={4}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2.5 }}>Booking Preferences</Typography>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel>Booking Type</InputLabel>
                          <Select value={formData.bookingType} label="Booking Type" onChange={handleChange('bookingType')}>
                            <MenuItem value="Rent">Rent</MenuItem>
                            <MenuItem value="Buy">Buy</MenuItem>
                            <MenuItem value="Sell">Sell</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel>Payment Method</InputLabel>
                          <Select value={formData.paymentMethod} label="Payment Method" onChange={handleChange('paymentMethod')}>
                            <MenuItem value="PayFast">PayFast</MenuItem>
                            <MenuItem value="Stripe">Stripe</MenuItem>
                            <MenuItem value="CashCounter">Cash Counter</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>

                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2.5 }}>Personal Information</Typography>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth label="First Name" value={formData.customerFirstName} onChange={handleChange('customerFirstName')} required />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Last Name" value={formData.customerLastName} onChange={handleChange('customerLastName')} required />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Email Address" type="email" value={formData.customerEmail} onChange={handleChange('customerEmail')} required />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Phone Number" value={formData.customerPhone} onChange={handleChange('customerPhone')} />
                      </Grid>
                    </Grid>
                  </Box>

                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2.5 }}>Address Details</Typography>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12}>
                        <TextField fullWidth label="Street Address" value={formData.customerAddress} onChange={handleChange('customerAddress')} />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField fullWidth label="City" value={formData.customerCity} onChange={handleChange('customerCity')} />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField fullWidth label="Country" value={formData.customerCountry} onChange={handleChange('customerCountry')} />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField fullWidth label="Postal Code" value={formData.customerPostalCode} onChange={handleChange('customerPostalCode')} />
                      </Grid>
                    </Grid>
                  </Box>

                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2.5 }}>Schedule & Occupancy</Typography>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Move In Date" type="date" value={formData.moveInDate} onChange={handleChange('moveInDate')} InputLabelProps={{ shrink: true }} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Move Out Date" type="date" value={formData.moveOutDate} onChange={handleChange('moveOutDate')} InputLabelProps={{ shrink: true }} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Adults" type="number" value={formData.numberOfAdults} onChange={handleChange('numberOfAdults')} inputProps={{ min: 0 }} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Children" type="number" value={formData.numberOfChildren} onChange={handleChange('numberOfChildren')} inputProps={{ min: 0 }} />
                      </Grid>
                    </Grid>
                  </Box>

                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2.5 }}>Additional Notes</Typography>
                    <TextField fullWidth multiline minRows={4} label="Notes / Requirements" value={formData.notes} onChange={handleChange('notes')} />
                  </Box>

                  <Box sx={{ pt: 1 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={!isFormValid() || submitting}
                      sx={{ py: 1.8, borderRadius: 3, fontWeight: 800, minWidth: 260 }}
                    >
                      {submitting ? 'Processing Booking...' : `Continue with ${formData.paymentMethod}`}
                    </Button>
                  </Box>
                </Stack>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default PropertyBooking;
